import * as Tabs from "@radix-ui/react-tabs";
import * as Select from "@radix-ui/react-select";
import * as Menubar from "@radix-ui/react-menubar";
import { LeadsPanel } from "./LeadsPanel";
import { forwardRef, ReactNode, useState } from "react";
import { getCSV } from "../../../services/api";
import { Spinner } from "../../ui/Spinner";
import { Combobox } from "../../ui/Combobox";

const items = [
  { value: "name", label: "Name" },
  { value: "address", label: "Address" },
  { value: "lead_owner", label: "Lead Owner" },
  { value: "state", label: "State" },
  { value: "zip_code", label: "Zip Code" },
  { value: "phone", label: "Phone" },
  { value: "lead_status", label: "Status" },
];

function getLabelByValue(value: string) {
  const item = items.find((item) => item.value === value);
  return item ? item.label : null;
}

export const LeadTabs = ({
  search,
  setSearch,
}: {
  search: string;
  setSearch: (val: string) => void;
}) => {
  const [activeTab, setActiveTab] = useState("0");
  const [exporting, setExporting] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string[]>([]);
  const [filters, setFilters] = useState<any>();

  const downloadCSV = async () => {
    try {
      setExporting(true);
      const response = await getCSV({ lead_type: activeTab });
      if (!response) return;
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = "leads.csv"; // Specify the file name here
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url); // Clean up

      document.body.removeChild(a);
    } catch (error) {
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const onFilterChange = (val: string) => {
    setSelectedFilter((state) =>
      state.includes(val) ? state : [...state, val]
    );
  };

  const onFilterRemove = (val: string) => {
    setSelectedFilter((state) => state.filter((itm) => val !== itm));
  };

  const onFilterSave = (filterName: string, value: any) => {
    if (value.length === 0) {
      const newState = {...filters};
      delete newState[filterName];
      setFilters(newState);
      return;
    }
    setFilters((state: any) => ({ ...state, [filterName]: value }));
  };

  return (
    <div>
      <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
        <Tabs.List className="flex flex-col md:flex-row items-start md:items-center justify-between gap-y-6 mt-6">
          <div className="text-sm font-semibold text-gray-800">
            <Tabs.Trigger
              className="px-4 py-3 border radix-state-active:border-primary"
              value="0"
            >
              All Leads
            </Tabs.Trigger>
            <Tabs.Trigger
              className="px-4 py-3 border radix-state-active:border-primary"
              value="1"
            >
              My Leads
            </Tabs.Trigger>
            <Tabs.Trigger
              className="px-4 py-3 border radix-state-active:border-primary"
              value="2"
            >
              My Team Leads
            </Tabs.Trigger>
            <Tabs.Trigger
              className="px-4 py-3 border radix-state-active:border-primary"
              value="3"
            >
              My Favourite
            </Tabs.Trigger>
            <Tabs.Trigger
              className="px-4 py-3 border radix-state-active:border-primary"
              value="4"
            >
              My Areas
            </Tabs.Trigger>
          </div>

          <button
            className="flex items-center gap-x-2 text-sm font-semibold px-4 py-3 text-gray-600 border-2"
            onClick={downloadCSV}
            disabled={exporting}
          >
            {exporting ? <Spinner /> : null}
            <span>Export CSV</span>
          </button>

          <FilterSelect onSelect={onFilterChange} />

          <div className="w-full md:w-1/4">
            <input
              type="text"
              name="search"
              onChange={(e) => setSearch(e.target.value)}
              value={search}
              placeholder="Search"
              className="border px-4 py-3 w-full"
            />
          </div>
        </Tabs.List>

        <div className="mt-6 flex gap-x-4">
          {selectedFilter.map((filter) => (
            <Combobox
              key={filter}
              filter={filter}
              label={getLabelByValue(filter) || ""}
              onRemove={onFilterRemove}
              leadType={Number(activeTab)}
              onFilterSave={onFilterSave}
            />
          ))}
        </div>

        <Tabs.Content value="0">
          <LeadsPanel activeIndex={0} search={search} filters={filters} />
        </Tabs.Content>
        <Tabs.Content value="1">
          <LeadsPanel activeIndex={1} search={search} filters={filters} />
        </Tabs.Content>
        <Tabs.Content value="2">
          <LeadsPanel activeIndex={2} search={search} filters={filters} />
        </Tabs.Content>
        <Tabs.Content value="3">
          <LeadsPanel activeIndex={3} search={search} filters={filters} />
        </Tabs.Content>
        <Tabs.Content value="4">
          <LeadsPanel activeIndex={4} search={search} filters={filters} />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
};

const FilterSelect = ({ onSelect }: { onSelect: (val: string) => void }) => {
  return (
    <Menubar.Root>
      <Menubar.Menu>
        <Menubar.Trigger className="flex items-center gap-x-2 text-sm font-semibold px-4 py-3 text-gray-700 border">
          <span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.8215 4.72239C2.19121 4.01796 1.87607 3.66574 1.86418 3.3664C1.85386 3.10636 1.9656 2.85643 2.16628 2.69074C2.39728 2.5 2.86991 2.5 3.81515 2.5H16.1843C17.1295 2.5 17.6021 2.5 17.8331 2.69074C18.0338 2.85643 18.1456 3.10636 18.1352 3.3664C18.1233 3.66574 17.8082 4.01796 17.1779 4.72239L12.4227 10.037C12.2971 10.1774 12.2343 10.2477 12.1895 10.3276C12.1497 10.3984 12.1206 10.4747 12.1029 10.554C12.083 10.6435 12.083 10.7377 12.083 10.9261V15.382C12.083 15.5449 12.083 15.6264 12.0568 15.6969C12.0335 15.7591 11.9958 15.8149 11.9466 15.8596C11.8909 15.9102 11.8153 15.9404 11.664 16.001L8.83063 17.1343C8.52435 17.2568 8.3712 17.3181 8.24827 17.2925C8.14076 17.2702 8.04642 17.2063 7.98575 17.1148C7.91637 17.0101 7.91637 16.8452 7.91637 16.5153V10.9261C7.91637 10.7377 7.91637 10.6435 7.89647 10.554C7.87883 10.4747 7.84968 10.3984 7.80995 10.3276C7.76516 10.2477 7.70233 10.1774 7.57669 10.037L2.8215 4.72239Z"
                stroke="#475467"
                stroke-width="1.66667"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <span>Filter</span>
        </Menubar.Trigger>

        <Menubar.Portal container={document.querySelector("[role='dialog']")}>
          <Menubar.Content className="overflow-hidden bg-white rounded-md z-50 mt-2 border shadow-lg">
            {items.map((i) => (
              <SelectItem onSelect={onSelect} value={i.value}>
                {i.label}
              </SelectItem>
            ))}
          </Menubar.Content>
        </Menubar.Portal>
      </Menubar.Menu>
    </Menubar.Root>
  );
};

const SelectItem = ({
  value,
  children,
  onSelect,
}: {
  value: string;
  children: ReactNode;
  onSelect: (val: string) => void;
}) => {
  return (
    <Menubar.Item
      onClick={() => onSelect(value)}
      className="px-5 py-3 text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100 hover:font-semibold transition"
    >
      {children}
    </Menubar.Item>
  );
};
